import { hexToRgbArray } from '@movingimage-evp/mi-ui-component-library';

/**
 * Calculates the relative luminance of a color based on its RGB components.
 * Uses the WCAG 2.0 formula for relative luminance calculation.
 * @param r - Red component (0-255).
 * @param g - Green component (0-255).
 * @param b - Blue component (0-255).
 * @returns The relative luminance (0 to 1).
 */
export function getRelativeLuminance(r: number, g: number, b: number): number {
  const [rs, gs, bs] = [r, g, b].map((c) => {
    const sRGB = c / 255;
    return sRGB <= 0.03928 ? sRGB / 12.92 : Math.pow((sRGB + 0.055) / 1.055, 2.4);
  });

  return 0.2126 * rs + 0.7152 * gs + 0.0722 * bs;
}

/**
 * Expands 3-character hex codes to 6-character format.
 * @param hex - The hex color string (e.g., '#RGB' or '#RRGGBB').
 * @returns The expanded 6-character hex code (e.g., '#RRGGBB').
 */
function expandHexCode(hex: string): string {
  if (hex.length === 4 && hex.startsWith('#')) {
    return '#' + hex[1] + hex[1] + hex[2] + hex[2] + hex[3] + hex[3];
  }

  return hex;
}

/**
 * Determines if a hex color is dark based on its relative luminance.
 * Optionally scales the color toward black based on the provided factor before evaluation.
 * @param hex - The hex color string (e.g., '#RRGGBB' or '#RGB').
 * @param mixPercentage - Factor indicating how much of the original color to retain (0 to 1). 1 = original color (no darkening), 0 = fully black. Use when the primary color is darkened in CSS (for example 'var(--mi-primary-70)').
 * @returns True if the color is dark, false otherwise.
 */
export function isColorDark(hex: string, mixPercentage = 1): boolean {
  const expandedHex = expandHexCode(hex);
  const rgb = hexToRgbArray(expandedHex);
  const mixedRgb = rgb.map((channel) => channel * mixPercentage);
  const luminance = getRelativeLuminance(mixedRgb[0], mixedRgb[1], mixedRgb[2]);

  return luminance < 0.5;
}
